import { useState, useEffect, useCallback, useRef } from 'react';
import TeamsLogo from '../components/TeamsLogo';
import { fetchMeetingUrl } from '../lib/config';
import { safeB64, isValidEmail } from '../utils';

const BookingPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const nameRef = useRef<HTMLInputElement>(null);

  const handleGenerate = useCallback(async () => {
    let valid = true;

    if (!name.trim()) {
      setNameError(true);
      valid = false;
    } else {
      setNameError(false);
    }

    const emailVal = email.trim();
    if (emailVal && !isValidEmail(emailVal)) {
      setEmailError(true);
      valid = false;
    } else {
      setEmailError(false);
    }

    if (!valid) return;

    const meetUrl = await fetchMeetingUrl(name, emailVal || undefined);
    if (!meetUrl) return;

    const b64Name = safeB64(name);
    const b64Email = email ? safeB64(email) : 'nm';

    (document.getElementById('res-name-tag') as HTMLElement).textContent = 'For: ' + name;
    (document.getElementById('res-link-text') as HTMLElement).textContent = meetUrl;
    (document.getElementById('res-name-plain') as HTMLElement).textContent = name;
    (document.getElementById('res-email-plain') as HTMLElement).textContent = email || '(not provided — name only link)';
    (document.getElementById('res-p-val') as HTMLElement).textContent = b64Name;
    (document.getElementById('res-path-val') as HTMLElement).textContent = b64Email;

    setShowResult(true);
    setCopied(false);
    
    const btnGen = document.getElementById('btn-gen') as HTMLElement;
    const btnNew = document.getElementById('btn-new') as HTMLElement;
    const resultPanel = document.getElementById('result-panel');
    
    if (btnGen) btnGen.style.display = 'none';
    if (btnNew) btnNew.style.display = 'flex';
    if (resultPanel) resultPanel.classList.add('show');

    setTimeout(() => {
      if (resultPanel) {
        resultPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  }, [name, email]);

  const handleCopy = useCallback(async () => {
    const text = (document.getElementById('res-link-text') as HTMLElement)?.textContent || '';
    const btn = document.getElementById('btn-copy');
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (btn) {
        btn.innerHTML = `<svg viewBox="0 0 20 20" fill="currentColor" style="width:13px;height:13px"><path d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.58l7.3-7.3a1 1 0 0 1 1.4 0Z"/></svg> Copied!`;
        btn.classList.add('copied');
      }
      setTimeout(() => {
        setCopied(false);
        if (btn) {
          btn.innerHTML = `<svg viewBox="0 0 20 20" fill="currentColor" style="width:13px;height:13px"><path d="M7 2a2 2 0 0 0-2 2v1H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H8a2 2 0 0 0-1 .27V4a2 2 0 0 0-2-2H7Zm0 1h0a1 1 0 0 1 1 1v1H6V4a1 1 0 0 1 1-1Zm1 3h7a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-1V7a2 2 0 0 0-2-2H7V4h1v2Zm-4 1h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z"/></svg> Copy link`;
          btn.classList.remove('copied');
        }
      }, 2500);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }, []);

  const handleReset = useCallback(() => {
    setName('');
    setEmail('');
    setNameError(false);
    setEmailError(false);
    setShowResult(false);
    setCopied(false);
    
    const resultPanel = document.getElementById('result-panel');
    const btnGen = document.getElementById('btn-gen');
    const btnNew = document.getElementById('btn-new');
    
    if (resultPanel) resultPanel.classList.remove('show');
    if (btnGen) btnGen.style.display = '';
    if (btnNew) btnNew.style.display = 'none';
    
    // Clear the result content
    (document.getElementById('res-name-tag') as HTMLElement).textContent = '';
    (document.getElementById('res-link-text') as HTMLElement).textContent = '';
    (document.getElementById('res-name-plain') as HTMLElement).textContent = '';
    (document.getElementById('res-email-plain') as HTMLElement).textContent = '';
    (document.getElementById('res-p-val') as HTMLElement).textContent = '';
    (document.getElementById('res-path-val') as HTMLElement).textContent = '';
    
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !showResult) {
        handleGenerate();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [handleGenerate, showResult]);

  return (
    <>
      <style>{`
        :root {
          --brand-purple: #6264a7;
          --brand-purple-dk: #3d3f8e;
          --brand-purple-lt: #bdbde6;
          --brand-bg: #f0f0f0;
          --card-bg: #ffffff;
          --text: #252423;
          --text-mid: #616161;
          --text-light: #9a9a9a;
          --border: #d1d1d1;
          --red: #c4314b;
          --green: #107c10;
          --r-sm: 4px;
          --r-md: 8px;
          --r-lg: 14px;
          --shadow: 0 4px 24px rgba(0,0,0,.11);
          --font: "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
        }
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        html, body {
          min-height: 100%;
          font-family: var(--font);
          font-size: 14px;
          color: var(--text);
          background: var(--brand-bg);
          -webkit-font-smoothing: antialiased;
          user-select: none;
          -webkit-user-select: none;
        }
        a {
          color: var(--brand-purple);
          text-decoration: none;
        }
        a:hover {
          text-decoration: underline;
        }
        button {
          font-family: var(--font);
          cursor: pointer;
          border: none;
        }
        input, textarea, select {
          font-family: var(--font);
          user-select: text;
          -webkit-user-select: text;
          caret-color: var(--brand-purple);
        }
        .page-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: 40px 16px 60px;
        }
        .header-row {
          width: 100%;
          max-width: 540px;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 32px;
        }
        .brand {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .brand span {
          font-size: 15px;
          font-weight: 700;
          color: var(--text);
        }
        .header-label {
          font-size: 12px;
          color: var(--text-light);
          margin-left: auto;
        }
        .card {
          background: var(--card-bg);
          border-radius: var(--r-lg);
          box-shadow: var(--shadow);
          padding: 36px 40px 32px;
          width: 100%;
          max-width: 540px;
          animation: fadeUp 0.35s ease both;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .card-title {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 6px;
          color: var(--text);
        }
        .card-subtitle {
          font-size: 13px;
          color: var(--text-mid);
          margin-bottom: 28px;
          line-height: 1.5;
        }
        .form-group {
          margin-bottom: 18px;
        }
        .form-group label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-mid);
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .form-group input {
          width: 100%;
          border: 1.5px solid var(--border);
          border-radius: var(--r-sm);
          padding: 10px 13px;
          font-size: 14px;
          color: var(--text);
          background: #fff;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .form-group input:focus {
          border-color: var(--brand-purple);
          box-shadow: 0 0 0 3px rgba(98, 100, 167, 0.15);
        }
        .form-group input.error {
          border-color: var(--red);
        }
        .error-text {
          font-size: 11px;
          color: var(--red);
          margin-top: 4px;
          display: none;
        }
        .error-text.show {
          display: block;
        }
        .hint-text {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        .divider {
          height: 1px;
          background: var(--border);
          margin: 24px 0;
        }
        .btn-primary {
          width: 100%;
          background: var(--brand-purple);
          color: #fff;
          border-radius: var(--r-sm);
          padding: 12px 20px;
          font-size: 14px;
          font-weight: 700;
          transition: background 0.15s, transform 0.1s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .btn-primary:hover {
          background: var(--brand-purple-dk);
        }
        .btn-primary:active {
          transform: scale(0.99);
        }
        .btn-primary svg {
          width: 16px;
          height: 16px;
        }
        .result-panel {
          margin-top: 24px;
          display: none;
          border: 1.5px solid var(--brand-purple-lt);
          border-radius: var(--r-md);
          overflow: hidden;
          animation: fadeUp 0.3s ease both;
        }
        .result-panel.show {
          display: block;
        }
        .result-header {
          background: #f0f0fc;
          padding: 10px 14px;
          font-size: 11px;
          font-weight: 700;
          color: var(--brand-purple);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .result-header .name-tag {
          font-size: 13px;
          font-weight: 600;
          color: var(--text);
          text-transform: none;
          letter-spacing: 0;
        }
        .result-link-row {
          padding: 14px;
          background: #fff;
          display: flex;
          align-items: center;
          gap: 10px;
          border-bottom: 1px solid var(--border);
        }
        .result-link-text {
          flex: 1;
          font-size: 12px;
          color: var(--text-mid);
          word-break: break-all;
          line-height: 1.5;
          background: #f8f8f8;
          border-radius: var(--r-sm);
          padding: 8px 10px;
          border: 1px solid var(--border);
          user-select: all;
        }
        .btn-copy {
          flex-shrink: 0;
          background: var(--brand-purple);
          color: #fff;
          border-radius: var(--r-sm);
          padding: 9px 14px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: background 0.15s;
          white-space: nowrap;
        }
        .btn-copy:hover {
          background: var(--brand-purple-dk);
        }
        .btn-copy.copied {
          background: var(--green);
        }
        .btn-copy svg {
          width: 13px;
          height: 13px;
        }
        .result-details {
          padding: 12px 14px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .detail-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 12px;
        }
        .detail-label {
          min-width: 90px;
          color: var(--text-light);
          font-weight: 600;
          flex-shrink: 0;
        }
        .detail-value {
          color: var(--text);
          word-break: break-all;
          font-family: monospace;
          font-size: 11px;
          background: #f5f5f5;
          padding: 2px 6px;
          border-radius: 3px;
        }
        .btn-new {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 16px;
          font-size: 13px;
          color: var(--brand-purple);
          font-weight: 600;
          background: none;
          padding: 0;
          transition: color 0.14s;
        }
        .btn-new:hover {
          color: var(--brand-purple-dk);
        }
        .btn-new svg {
          width: 14px;
          height: 14px;
        }
        .footer {
          margin-top: 32px;
          font-size: 11px;
          color: var(--text-light);
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
          justify-content: center;
        }
        .footer a {
          color: var(--text-light);
        }
        .footer a:hover {
          color: var(--text-mid);
        }
        .ms-row {
          display: flex;
          align-items: center;
          gap: 5px;
        }
      `}</style>

      <div className="page-container">
        <div className="header-row">
          <div className="brand">
            <TeamsLogo size={28} />
            <span>ZoneLayer Teams</span>
          </div>
          <span className="header-label">Meeting Link Generator</span>
        </div>

        <div className="card">
          <div className="card-title">Create a meeting link</div>
          <div className="card-subtitle">
            Enter the participant's details below. A personalised meeting link
            will be generated for you to share.
          </div>

          <div id="booking-form">
            <div className="form-group">
              <label htmlFor="f-name">
                Participant name <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input
                ref={nameRef}
                type="text"
                id="f-name"
                placeholder="e.g. Martins"
                autoComplete="name"
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={nameError ? 'error' : ''}
              />
              <div className={`error-text ${nameError ? 'show' : ''}`} id="err-name">
                Please enter the participant's name.
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="f-email">
                Participant email{' '}
                <span style={{ color: 'var(--text-light)', fontSize: '10px', fontWeight: 400 }}>
                  (optional)
                </span>
              </label>
              <input
                type="email"
                id="f-email"
                placeholder="e.g. martins@example.com"
                autoComplete="email"
                maxLength={120}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={emailError ? 'error' : ''}
              />
              <div className={`error-text ${emailError ? 'show' : ''}`} id="err-email">
                Please enter a valid email address.
              </div>
              <div className="hint-text">
                The email is encoded into the meeting path and used to
                personalise the destination page.
              </div>
            </div>

            <div className="divider"></div>

            <button className="btn-primary" id="btn-gen" onClick={handleGenerate}>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a8 8 0 1 0 0 16A8 8 0 0 0 10 2Zm1 4.5v3h3a1 1 0 1 1 0 2h-3v3a1 1 0 1 1-2 0v-3H6a1 1 0 1 1 0-2h3v-3a1 1 0 1 1 2 0Z" />
              </svg>
              Generate meeting link
            </button>
          </div>

          <div className="result-panel" id="result-panel">
            <div className="result-header">
              Meeting link generated
              <span className="name-tag" id="res-name-tag"></span>
            </div>
            <div className="result-link-row">
              <div className="result-link-text" id="res-link-text"></div>
              <button className={`btn-copy ${copied ? 'copied' : ''}`} id="btn-copy" onClick={handleCopy}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7 2a2 2 0 0 0-2 2v1H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H8a2 2 0 0 0-1 .27V4a2 2 0 0 0-2-2H7Zm0 1h0a1 1 0 0 1 1 1v1H6V4a1 1 0 0 1 1-1Zm1 3h7a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-1V7a2 2 0 0 0-2-2H7V4h1v2Zm-4 1h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
                </svg>
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>
            <div className="result-details">
              <div className="detail-row">
                <span className="detail-label">Name</span>
                <span className="detail-value" id="res-name-plain"></span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value" id="res-email-plain"></span>
              </div>
              <div className="detail-row">
                <span className="detail-label">?p= value</span>
                <span className="detail-value" id="res-p-val"></span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Path (email)</span>
                <span className="detail-value" id="res-path-val"></span>
              </div>
            </div>
          </div>

          <button
            className="btn-new"
            id="btn-new"
            style={{ display: showResult ? 'flex' : 'none' }}
            onClick={handleReset}
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a8 8 0 1 0 0 16A8 8 0 0 0 10 2Zm1 4.5v3h3a1 1 0 1 1 0 2h-3v3a1 1 0 1 1-2 0v-3H6a1 1 0 1 1 0-2h3v-3a1 1 0 1 1 2 0Z" />
            </svg>
            Generate another link
          </button>
        </div>

        <footer className="footer">
          <span className="ms-row">
            <svg width="13" height="13" viewBox="0 0 22 22">
              <rect x="0" y="0" width="10" height="10" fill="#f25022" />
              <rect x="12" y="0" width="10" height="10" fill="#7fba00" />
              <rect x="0" y="12" width="10" height="10" fill="#00a4ef" />
              <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
            </svg>
            ZoneLayer Teams
          </span>
          <a href="#">Privacy</a>
          <a href="#">Third-party disclosures</a>
        </footer>
      </div>
    </>
  );
};

export default BookingPage;
